<template>
  <div class="case-copy-tool new-globals">
    <md-card class="selection-card">
      <md-progress-bar v-if="loading" md-mode="indeterminate" />
      <md-card-header>
        <div class="md-title">{{ title }}</div>
        <div class="md-subhead">Select a text field to modify its content</div>
      </md-card-header>

      <md-card-content>
        <span v-if="error" class="error">{{
          error.message || error.error || error
        }}</span>
        <div>
          <md-field>
            <label>Environment</label>
            <md-select :value="fromEnv" @input="(env) => updateFromEnv(env)">
              <md-option value="dev">Dev</md-option>
              <md-option value="stable">Stable</md-option>
              <md-option value="staging">Staging</md-option>
              <md-option value="app">Prod</md-option>
            </md-select>
          </md-field>
        </div>
        <div>
          <md-autocomplete
            ref="fromOrgAutocomplete"
            placeholder="Organization"
            :md-options="fromOrgs"
            :disabled="loading"
            value
            :md-open-on-focus="true"
            :md-fuzzy-search="false"
            @md-selected="updateSelectedFromOrg"
          >
            <label>Organization</label>
            <template slot="md-autocomplete-item" slot-scope="{ item }">
              {{
                item.name
              }}
            </template>
          </md-autocomplete>
        </div>
        <div>
          <md-autocomplete
            ref="fromUsersAutocomplete"
            placeholder="Users"
            :md-options="fromUsersWithLabel"
            :disabled="loading"
            value
            :md-open-on-focus="true"
            :md-fuzzy-search="false"
            @md-selected="updateSelectedFromUser"
          >
            <label>Users</label>
            <template slot="md-autocomplete-item" slot-scope="{ item }">
              {{
                item.label
              }}
            </template>
          </md-autocomplete>
        </div>
        <div v-if="!disableCaseSelection">
          <md-field
            class="md-autocomplete md-focused md-active"
            :style="{ background: 'white' }"
            md-menu-trigger
          >
            <md-input v-model="state.searchTerm" />
            <md-icon>search</md-icon>
          </md-field>
          <md-field>
            <label>Case Name</label>
            <md-select
              placeholder="Case Name"
              :multiple="!singleSelect"
              :value="singleSelect ? state.selectedCaseIds[0] : state.selectedCaseIds"
              @md-selected="value => singleSelect ? updateSelectedCases([value]) : updateSelectedCases(value)"
            >
              <md-menu-item v-if="!singleSelect">
                <md-checkbox v-model="isAllCases" class="md-primary">
                  All Cases
                </md-checkbox>
              </md-menu-item>
              <md-option
                v-for="caseConf in filteredCases"
                :key="caseConf.id"
                :value="caseConf.id"
              >
                {{ caseConf.name }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

export default {
  name: 'user-case-selector',
  components: {},
  props: {
    title: {
      type: String,
      required: true,
    },
    fromEnv: {
      type: String,
      default: 'dev',
    },
    toEnv: {
      type: String,
      default: 'dev',
    },
    fromOrgs: {
      type: Array,
      default: () => [],
    },
    fromCases: {
      type: Array,
      default: () => [],
    },
    fromUsers: {
      type: Array,
      default: () => [],
    },
    toOrgs: {
      type: Array,
      default: () => [],
    },
    toUsers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    copying: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Error, Object],
      default: null,
    },
    disableCaseSelection: {
      type: Boolean,
      default: false,
    },
    singleSelect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        selectedFromOrg: null,
        selectedFromUser: null,
        selectedToOrg: null,
        selectedToUser: null,
        selectedCaseIds: [],
        searchTerm: '',
      },
    };
  },
  computed: {
    fromUsersWithLabel() {
      return this.fromUsers.map((user) => ({
        ...user,
        label: this.userLabel(user),
      }));
    },
    filteredCases() {
      if (this.state.searchTerm) {
        return this.fromCases.filter((caseConf) =>
          caseConf.name
            .toLowerCase()
            .includes(this.state.searchTerm.trim().toLowerCase())
        );
      }
      return this.fromCases;
    },
    isAllCases: {
      get() {
        const selectedIds = sortBy(this.state.selectedCaseIds);
        const filteredIds = sortBy(
          this.filteredCases.map((caseConf) => caseConf.id)
        );
        return isEqual(selectedIds, filteredIds);
      },
      set() {
        if (this.isAllCases) {
          this.unselectAllCases();
        } else {
          this.selectAllCases();
        }
      },
    },
  },
  methods: {
    userLabel(user) {
      if (user.given_name || user.family_name) {
        return `${user.given_name || ''} ${user.family_name || ''}`;
      }
      return user.email;
    },
    updateFromEnv(env) {
      this.$emit('updateFromEnv', env);
      this.$emit('updateFromCases', this.state.selectedFromOrg, this.state.selectedFromUser);
      this.$emit('envSelected', env);
    },
    updateSelectedFromOrg(org) {
      this.state.selectedFromOrg = org;
      this.updateFromUsers();
      this.$nextTick(() => {
        // Force close the autocomplete, vue material bug
        if (this.$refs.fromOrgAutocomplete) {
          this.$refs.fromOrgAutocomplete.showMenu = false;
        }
      });
      this.$emit('orgSelected', org);
    },
    updateSelectedFromUser(user) {
      this.state.selectedFromUser = user;
      this.updateFromCases();
      this.$nextTick(() => {
        // Force close the autocomplete, vue material bug
        if (this.$refs.fromUsersAutocomplete) {
          this.$refs.fromUsersAutocomplete.showMenu = false;
        }
      });
      this.$emit('userSelected', user);
    },
    updateFromCases() {
      this.$emit(
        'updateFromCases',
        this.state.selectedFromOrg,
        this.state.selectedFromUser
      );
    },
    updateFromUsers() {
      this.$emit('updateFromUsers', this.state.selectedFromOrg);
    },
    updateToUsers() {
      this.$emit('updateToUsers', this.state.selectedToOrg);
    },
    updateSelectedCases(caseIds) {
      this.state.selectedCaseIds = caseIds;
      this.$emit('casesSelected', caseIds);
    },
    selectAllCases() {
      this.updateSelectedCases(
        this.filteredCases.map((caseConf) => caseConf.id)
      );
    },
    unselectAllCases() {
      this.updateSelectedCases([]);
    },
  },
};
</script>

<style scoped>
.case-copy-tool {
  display: flex;
  flex-wrap: wrap;
}

.selection-card {
  max-width: 400px;
}

.error {
  color: red;
}
</style>